import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { EMPTY, finalize, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { LocalStorageClass } from '../../helpers/local-storage-helper';
import {
  ApprovalStatusResponse,
  AuthResponse,
  OtpRequestInfo,
  SignInLookupResponse,
  SignUpLookupResponse,
} from '../../types/auth/auth.type';
import {
  GetUserTenantsResponse,
  IUserTenantsAttributes,
  SwitchTenantResponse,
} from '../../types/auth/user-tenants.type';

const endpoints = {
  userTenants: '/user/tenants',
  switchTenant: '/user/switch-tenant',
  logout: '/logout',
  refreshToken: '/refresh-token',
  // --- v2 (lookup-driven) auth — mirrors the mobile onboarding_v3 flow ---
  v2SignInLookup: '/v2/auth/signin/lookup',
  v2SignInPassword: '/v2/auth/signin/password',
  v2SignInPhone: '/v2/auth/signin/phone',
  v2SignInPhoneVerify: '/v2/auth/signin/phone/verify',
  v2PasswordResetRequest: '/v2/auth/password/reset/request',
  v2PasswordReset: '/v2/auth/password/reset',
  v2SignUpLookup: '/v2/auth/signup/lookup',
  v2SignUpActivationResend: '/v2/auth/signup/activation/resend',
  v2SignUpActivate: '/v2/auth/signup/activate',
  v2SignUpPhone: '/v2/auth/signup/phone',
  v2SignUpPhoneVerify: '/v2/auth/signup/phone/verify',
  v2Register: '/v2/auth/register',
  v2ApprovalResend: '/v2/auth/register/approval/resend',
  v2ApprovalStatus: '/v2/auth/register/approval/status',
};

/**
 * The v2 lookup/sign-in endpoints are anonymous: the backend resolves the
 * tenant from the email/phone/token in the request body, so the stored JWT
 * (if any) must NOT be attached.
 */
const ANONYMOUS_HEADERS = new HttpHeaders({
  'Content-Type': 'application/json',
  excludeJwt: 'true',
});

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);

  readonly tenants = signal<IUserTenantsAttributes[]>([]);

  switchCurrentUserTenants(tenant: IUserTenantsAttributes) {
    const url = `${environment.baseApiRoute}${endpoints.switchTenant}`;
    return this.http.post<SwitchTenantResponse>(url, {
      tenant: tenant.tenant_identifier,
      invalidatePreviousTokens: false,
    });
  }

  getCurrentUserTenants() {
    const url = `${environment.baseApiRoute}${endpoints.userTenants}`;
    return this.http.get<GetUserTenantsResponse>(url);
  }

  loginSelectTenant(tenant: IUserTenantsAttributes, jwt: string) {
    const url = `${environment.baseApiRoute}${endpoints.switchTenant}`;
    const headers = new HttpHeaders({
      Authorization: jwt,
      accept: 'application / vnd.api + json',
      excludeJwt: 'true',
    });
    return this.http.post<SwitchTenantResponse>(
      url,
      { tenant: tenant.tenant_identifier, invalidatePreviousTokens: false },
      { headers },
    );
  }

  logout() {
    const url = `${environment.baseApiRoute}${endpoints.logout}`;
    return this.http.post(url, {}).pipe(
      finalize(() => {
        LocalStorageClass.clearUserData();
        window.location.reload();
      }),
    );
  }

  refreshCurrentToken() {
    const url = `${environment.baseApiRoute}${endpoints.refreshToken}`;
    const refreshToken = LocalStorageClass.getRefreshToken();

    // Never hit the refresh endpoint without a refresh token — it would 400 and,
    // on startup, can cascade into a spurious logout.
    if (!refreshToken) {
      return EMPTY;
    }

    const data = { refreshToken };
    return this.http.post(url, data, { headers: { excludeJwt: 'true' } }).pipe(
      tap((response: any) => {
        LocalStorageClass.setToken(response?.token, response?.refreshToken);
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // v2 (lookup-driven) auth — mirrors the mobile onboarding_v3 flow.
  // All endpoints below are anonymous: the backend resolves the tenant from the
  // identifier/token in the body, so no JWT is attached (excludeJwt).
  // ---------------------------------------------------------------------------

  signInLookup(email: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignInLookup}`;
    return this.http.post<SignInLookupResponse>(url, { email }, { headers: ANONYMOUS_HEADERS });
  }

  signInPassword(email: string, password: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignInPassword}`;
    return this.http.post<AuthResponse>(url, { email, password }, { headers: ANONYMOUS_HEADERS });
  }

  signInPhone(phoneNumber: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignInPhone}`;
    return this.http.post<OtpRequestInfo>(url, { phoneNumber }, { headers: ANONYMOUS_HEADERS });
  }

  signInPhoneVerify(phoneNumber: string, code: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignInPhoneVerify}`;
    return this.http.post<AuthResponse>(
      url,
      { phoneNumber, code },
      { headers: ANONYMOUS_HEADERS },
    );
  }

  passwordResetRequest(email: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2PasswordResetRequest}`;
    return this.http.post(url, { email }, { headers: ANONYMOUS_HEADERS });
  }

  passwordReset(token: string, password: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2PasswordReset}`;
    return this.http.post(url, { token, password }, { headers: ANONYMOUS_HEADERS });
  }

  signUpLookup(identifier: { email?: string; phoneNumber?: string }) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignUpLookup}`;
    return this.http.post<SignUpLookupResponse>(url, identifier, { headers: ANONYMOUS_HEADERS });
  }

  activationResend(email: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignUpActivationResend}`;
    return this.http.post(url, { email }, { headers: ANONYMOUS_HEADERS });
  }

  activateAccount(token: string, password: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignUpActivate}`;
    return this.http.post<AuthResponse>(
      url,
      { token, password },
      { headers: ANONYMOUS_HEADERS },
    );
  }

  signUpPhone(phoneNumber: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignUpPhone}`;
    return this.http.post<OtpRequestInfo>(url, { phoneNumber }, { headers: ANONYMOUS_HEADERS });
  }

  signUpPhoneVerify(phoneNumber: string, code: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2SignUpPhoneVerify}`;
    return this.http.post<AuthResponse>(
      url,
      { phoneNumber, code },
      { headers: ANONYMOUS_HEADERS },
    );
  }

  registerV2(body: {
    token: string;
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    password?: string;
    passwordRepeat?: string;
  }) {
    const url = `${environment.baseApiRoute}${endpoints.v2Register}`;
    return this.http.post<{ accessToken: string }>(url, body, { headers: ANONYMOUS_HEADERS });
  }

  approvalResend(email: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2ApprovalResend}`;
    return this.http.post(url, { email }, { headers: ANONYMOUS_HEADERS });
  }

  /**
   * GET register/approval/status — needs the freshly minted JWT. The token is
   * passed explicitly (excludeJwt) so it works before being committed to
   * storage, avoiding the errorInterceptor's 401 → login bounce during the
   * post-OAuth/registration window.
   */
  approvalStatus(jwt: string) {
    const url = `${environment.baseApiRoute}${endpoints.v2ApprovalStatus}`;
    const headers = new HttpHeaders({
      Authorization: jwt,
      excludeJwt: 'true',
    });
    return this.http.get<ApprovalStatusResponse>(url, { headers });
  }
}
